'use client'

// Route-segment error boundary (wraps every page under the root layout).
// openspec: iso27001-soc2-readiness task 1.4
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { useEffect } from 'react'
import { reportError } from '@/lib/report-error'

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    reportError(error, { boundary: 'app/error.tsx', digest: error.digest })
  }, [error])

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center">
        <AlertTriangle className="mx-auto mb-4 h-8 w-8 text-destructive" aria-hidden="true" />
        <h1 className="mb-2 font-semibold text-foreground text-xl">Something went wrong / 發生錯誤</h1>
        <p className="mb-6 text-muted-foreground text-sm">
          The page hit an unexpected error. Retry, or go back to the dashboard. If it keeps happening, contact
          support and quote the reference below.
        </p>
        <div className="mb-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-sm text-white transition-colors hover:bg-primary/90"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            重試 / Retry
          </button>
          <a
            href="/dashboard"
            className="inline-flex items-center rounded-lg border border-border px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-secondary"
          >
            Dashboard
          </a>
        </div>
        {error.digest && (
          <p className="font-mono text-[11px] text-muted-foreground">
            ref: {error.digest}
          </p>
        )}
      </div>
    </div>
  )
}
